<template>
  <div>
    <!-- Header -->
    <data-table
      :key="`${tableKey}-${isCheckable}`"
      :tableOpts="tableOpts"
      :loadData="loadData"
      :apiMode="true"
      @cell-clicked="onCellClicked"
    />

    <list-item-form
      v-if="showModal"
      :item="itemToEdit"
      :config="modalConfig"
      @close="closeModal"
      @refresh-table="refreshTable"
    />
  </div>
</template>

<script>
import { reactive, toRefs } from 'vue';
import { useStore } from 'vuex';
import { cloneDeep, get } from 'lodash';
import { useToast } from 'vue-toastification';
import { DialogProgrammatic } from '@/components/Dialog';
import DataTable from '@/components/table-fields/DataTable.vue';
import ListItemForm from '@/components/modals/ListItemForm.vue';
import Companies from '@/models/Companies';

export default {
  name: 'QaQcIssueTypes',
  props: {
    searchText: {
      type: String,
      default: '',
    },
  },

  components: {
    'data-table': DataTable,
    'list-item-form': ListItemForm,
  },

  setup(props) {
    const store = useStore();
    const toast = useToast();

    const data = reactive({
      tableKey: 0,
      showModal: false,
      itemToEdit: {},
    });

    const getCompany = async () => {
      await store.getters.userPromise;
      const user = store.state.userData;
      return Companies.getOne(user.company);
    };

    const notifyMara = () => {
      const data = { pathInfo: { name: 'update-qaqc-issueType' } };
      window.parent.postMessage(JSON.stringify(data), '*');
    };

    const modalConfig = {
      label: 'QA/QC Issue Type',
      showSubCategories: false,
      showColor: false,
      saveFn: async (payload) => {
        const company = await getCompany();
        const name = payload.name.trim();
        // Check duplicate
        if (company.qaqcIssueTypes.some((t) => t.name === name)) {
          return { failedData: [{ _errorMsg: `The QA/QC Issue Type ${name} is Already Present` }] };
        }
        company.qaqcIssueTypes.push({ name });
        await Companies.updateQaqcTypes(company);
        store.dispatch('getCompanyData');
        notifyMara();
        return { data: [{ name }] };
      },
      updateFn: async (payload) => {
        const company = await getCompany();
        const name = payload.name.trim();
        const issueType = company.qaqcIssueTypes.find((t) => t._id === payload._id);
        if (issueType) {
          issueType.name = name;
          await Companies.updateQaqcTypes(company);
          store.dispatch('getCompanyData');
          notifyMara();
        }
      },
    };

    const tableOpts = {
      defaultSortField: 'name',
      defaultSortOrder: 'asc',
      stickeyHeaders: false,
      fields: [
        {
          title: 'Name',
          id: 'name',
          prop: 'name',
          name: 'field-generic-input',
          width: '400px',
          visible: true,
          alwaysVisible: true,
          inputProps: {
            type: 'text',
            openCard: false,
          },
        },
        {
          title: 'ACTIONS',
          name: 'table-field-actions',
          id: 'action',
          hideFromChooser: true,
          alwaysVisible: true,
          defaultVisible: true,
          width: '112px',
          permissionModule: 'app&projects',
          permissionKey: 'company_lists',
          inputProps: {
            showEdit: false,
            groupButtons() {
              return [
                {
                  icon: 'icon-edit',
                  event: 'edit',
                  tooltip: 'Edit Issue Type',
                  permissionModule: 'app&projects',
                  alterDataKey: 'lists',
                },
                {
                  icon: 'icon-bin',
                  event: 'delete',
                  tooltip: 'Delete Issue Type',
                  permissionModule: 'app&projects',
                  alterDataKey: 'lists',
                },
              ];
            },
          },
        },
      ],
    };

    const loadData = async (params) => {
      const company = await getCompany();
      let issueTypes = company.qaqcIssueTypes || [];
      const search = props.searchText || '';
      if (search) {
        issueTypes = issueTypes.filter((t) =>
          t.name.toLowerCase().includes(search.toLowerCase()),
        );
      }
      return {
        data: issueTypes,
        total: issueTypes.length,
      };
    };

    const refreshTable = () => {
      data.tableKey += 1;
    };

    const openModal = (item = {}) => {
      data.itemToEdit = cloneDeep(item);
      data.showModal = true;
    };

    const closeModal = () => {
      data.showModal = false;
      data.itemToEdit = {};
    };

    const onCellClicked = ({ type, data: rowData }) => {
      if (type === 'edit') {
        openModal(rowData);
      } else if (type === 'delete') {
        DialogProgrammatic.confirm({
          title: 'Delete QA/QC Issue Type',
          message: `Are you sure you want to delete "${rowData.name}"?`,
          cancelButton: 'Cancel',
          type: 'danger',
          onClose: () => {
            data.isCheckable = false;
            data.selectedRows = [];
          },onConfirm: async () => {
            try {
              const company = await getCompany();
              company.qaqcIssueTypes = company.qaqcIssueTypes.filter(
                (t) => t._id !== rowData._id,
              );
              await Companies.updateQaqcTypes(company);
              store.dispatch('getCompanyData');
              notifyMara();
              toast.success('QA/QC Issue Type deleted successfully');
              refreshTable();
            } catch (e) {
              toast.error(get(e, 'data.message') || 'Error deleting QA/QC Issue Type');
            }
          },
        });
      }
    };

    return {
      ...toRefs(data),
      modalConfig,
      tableOpts,
      loadData,
      onCellClicked,
      refreshTable,
      openModal,
      closeModal,
    };
  },
};
</script>

<style scoped>
</style>